// The Kasa drawer (Onur, 22.10.2026): the bar's one money button, "Kasa
// X TL", opens what the desk needs of the day's money — the cash that
// should be in the drawer now, who is handed what today, and the
// drawer's movements line by line. Ver on a row records the payout
// (PayoutSheets.tsx), for those who may (admin). It took the place of
// Günün parası and Ödenecek and their drawer (C, 14.10.2026): what
// never touches the drawer — havale, seans fees, open accounts — is a
// chat question now. Any earlier day is its closing. Money holders
// only: the door strips `totals` for everyone else, and every figure
// goes through gizli mod.

import type {ReactNode} from "react";
import type {BoardKasaMove, BoardTotals, KasaMoveKind} from "../api/contract";
import {Sheet} from "../actions/Sheets";
import {HOLDER_TR, MONTH_TR, capTr, trDative} from "../labels";
import {Icon} from "../ui/Icon";
import {useMoney} from "../ui/blind-context";

export type TotalsWhen = "today" | "past";

const round2 = (n: number): number => Math.round(n * 100) / 100;
const sum = (xs: {amountTL: number}[]): number => round2(xs.reduce((a, x) => a + x.amountTL, 0));

export function KasaSheet({t, when, date, asOf, onClose, onProvider, onPay, onDevir}: {
  t: BoardTotals; when: TotalsWhen;
  date: string;
  asOf: string; // HH:MM, Istanbul: when the board was put together
  onClose: () => void;
  // A danışman's row opens her own drawer.
  onProvider: (providerId: string) => void;
  // Ver, for whoever may record a payout: her payout sheet, Seda's devir.
  onPay?: (providerId: string) => void;
  onDevir?: () => void;
}) {
  const {tl} = useMoney();
  const k = t.kasa;
  const past = when === "past";
  const head = (
    <header className="sd-head">
      <div className="sd-who">
        <h2 className="sd-title dm-title">{past ? "Gün kapanışı" : "Kasa"}</h2>
        <CountChip count={k.count} when={when} />
        <button className="sd-round" aria-label="Kapat" onClick={onClose}><Icon name="x" /></button>
      </div>
    </header>
  );
  // A count that still matches the journal is what the drawer held.
  const counted = past && !!k.count && !k.count.stale;
  return (
    <Sheet className="seans-sheet day-money" head={head} onClose={onClose}>
      <section className="dm-card pd-hero" aria-label="Kasa">
        <span className="dm-label">{past ? "Kapanışta kasada" : "Kasada olması gereken"}</span>
        <Figure value={tl(Math.abs(k.balanceTL))} minus={k.balanceTL < 0} />
        <small>{counted ? "Akşam sayımında sayılan" : past ? "Kayıtlara göre" : `Kayıtlara göre · ${asOf}`}</small>
      </section>
      {!past && t.payouts.due && (
        <Payouts p={t.payouts} notes={k.devirNotes} onProvider={onProvider}
          onPay={onPay} onDevir={onDevir} />
      )}
      <Moves t={t} past={past} date={date} />
    </Sheet>
  );
}

function Figure({value, minus}: {value: string; minus?: boolean}) {
  const [num, cur] = value.endsWith(" TL") ? [value.slice(0, -3), "TL"] : [value, ""];
  return (
    <span className={minus ? "dm-fig pd-fig bad" : "dm-fig pd-fig"}>
      {minus && "−"}{num} {cur && <span className="cur">{cur}</span>}
    </span>
  );
}

// One card: a title with an optional figure at its right, then rows.
function Card({title, aside, children, foot}: {
  title: string; aside?: ReactNode; children: ReactNode; foot?: ReactNode;
}) {
  return (
    <section className="dm-card" aria-label={title}>
      <h3 className="dm-card-head"><span>{title}</span>{aside && <small>{aside}</small>}</h3>
      <ul className="dm-rows">{children}</ul>
      {foot}
    </section>
  );
}

// One row: what it is (and a small line under it), and its figure. A
// row of "Bugün verilecek" has a third column: Ver when there is
// something to hand over (give), empty when not (null).
function Row({k, sub, v, tone, kind, onClick, give}: {
  k: ReactNode; sub?: ReactNode; v: ReactNode; tone?: "ok" | "bad" | "soft" | "warn";
  kind?: "sub" | "sub end" | "total"; onClick?: () => void; give?: (() => void) | null;
}) {
  const body = (
    <>
      <span className="dm-k">{k}{sub && <small>{sub}</small>}</span>
      <span className={tone ? `dm-v ${tone}` : "dm-v"}>
        {v}{onClick && <Icon name="chev" />}
      </span>
    </>
  );
  const main = onClick ? <button className="dm-link" onClick={onClick}>{body}</button> : body;
  if (give === undefined) return <li className={kind ? `dm-row ${kind}` : "dm-row"}>{main}</li>;
  return (
    <li className="dm-row pay">
      {onClick ? main : <span className="dm-main">{body}</span>}
      {give ? <button className="dm-give" onClick={give}>Ver</button> : <span />}
    </li>
  );
}

// The evening count against the journal, as a chip by the title: not
// yet, matching, a gap booked, or a count the journal has moved from.
function CountChip({count, when}: {count: BoardTotals["kasa"]["count"]; when: TotalsWhen}) {
  const {tl} = useMoney();
  if (!count) {
    return <span className={when === "past" ? "dm-chip warn" : "dm-chip"}>
      sayım {when === "past" ? "girilmemiş" : "girilmedi"}
    </span>;
  }
  if (count.stale) return <span className="dm-chip warn">sayım tekrar girilmeli</span>;
  if (count.diffTL === 0) return <span className="dm-chip ok"><Icon name="check" />sayım tutuyor</span>;
  return (
    <span className="dm-chip warn">
      sayım {tl(Math.abs(count.diffTL))} {count.diffTL < 0 ? "eksik" : "fazla"}
    </span>
  );
}

// Today: who is handed what — checkout's payNowTL, never worked out by
// hand — and the most Seda can take. A row opens that danışman's
// drawer, where the number is taken apart; Ver opens the sheet that
// records it.
function Payouts({p, notes, onProvider, onPay, onDevir}: {
  p: BoardTotals["payouts"]; notes: string[];
  onProvider: (providerId: string) => void;
  onPay?: (providerId: string) => void; onDevir?: () => void;
}) {
  const {tl, num} = useMoney();
  const due = p.due ?? [];
  const paidTo = (providerId: string) => p.paid
    .filter((x) => !x.toSeda && x.providerId === providerId).reduce((a, x) => a + x.amountTL, 0);
  const sedaPaid = p.paid.filter((x) => x.toSeda).reduce((a, x) => a + x.amountTL, 0);
  const owner = p.ownerProviderId;
  const dueTL = round2(due.reduce((a, d) => a + d.payNowTL, 0));
  return (
    <Card title="Bugün verilecek" aside={dueTL > 0 ? `danışmanlara ${tl(dueTL)}` : undefined} foot={
      <div className="dm-foot">
        {p.shortTL > 0 && (
          <p className="warn">Kasa danışmanların hepsine yetmiyor: {tl(p.shortTL)} eksik. Sohbette sorun.</p>
        )}
        {notes.map((n) => <p key={n}>{n}</p>)}
        <p>
          {onPay ? "Ver ödeme ekranını açar; ödeme orada kaydedilir. " : ""}
          Rakam sohbetteki çıkış hesabıyla aynıdır; elle bir şey düşülmez.
          {onPay ? "" : " Ödeyince sohbette kaydedin."}
        </p>
      </div>
    }>
      {due.length === 0 && !p.seda && <Row k="Bugün verilecek danışman yok" v="" tone="soft" />}
      {due.map((d) => {
        const paid = paidTo(d.providerId);
        const sub = d.unrecorded.count > 0 ? `${d.unrecorded.count} seans daha kaydedilecek` :
          d.balanceTL < 0 ? `hesap −${num(Math.abs(d.balanceTL))}, sohbette sorun` :
            paid > 0 ? `bugün ${num(paid)} verildi` :
              !d.inToday ? "bugün seansı yok" : undefined;
        return (
          <Row key={d.providerId} k={d.name} sub={sub}
            v={d.payNowTL > 0 ? tl(d.payNowTL) : "yok"} tone={d.payNowTL > 0 ? undefined : "soft"}
            onClick={() => onProvider(d.providerId)}
            give={onPay ? (d.payNowTL > 0 ? () => onPay(d.providerId) : null) : undefined} />
        );
      })}
      {p.seda && (
        <Row k="Seda" sub={[`hakedişi ${num(p.seda.owedTL)}`,
          sedaPaid > 0 ? `bugün ${num(sedaPaid)} verildi` : ""].filter(Boolean).join(" · ")}
        v={`en fazla ${tl(p.seda.safeTL)}`}
        onClick={owner ? () => onProvider(owner) : undefined}
        give={onPay ? (onDevir && p.seda.safeTL > 0 ? onDevir : null) : undefined} />
      )}
    </Card>
  );
}

// The drawer line by line: what came in and went out, grouped the way
// the desk counts it, each group's lines under it, down to what should
// be in it. Payouts that never touched the drawer are said under it.
// A server older than the list gives the day's movement as one figure.
const GROUPS: KasaMoveKind[] = ["collection", "payout", "devir", "expense", "refund", "count", "other"];

function Moves({t, past, date}: {t: BoardTotals; past: boolean; date: string}) {
  const {tl, num} = useMoney();
  const k = t.kasa;
  const devredenTL = round2(k.balanceTL - k.deltaTL);
  const signed = (n: number, plus = true) =>
    n < 0 ? `−${tl(-n)}` : `${plus && n > 0 ? "+" : ""}${tl(n)}`;
  const day = `${Number(date.slice(8, 10))} ${MONTH_TR[Number(date.slice(5, 7)) - 1]}`;
  const elsewhere = t.payouts.paid.filter((x) => x.from !== "kasa");

  const rows: ReactNode[] = [
    <Row key="devreden" k="Devreden" sub="önceki günden" v={tl(devredenTL)} />,
  ];
  if (!k.moves) {
    rows.push(<Row key="delta" k="Günün hareketi" v={signed(k.deltaTL)} />);
  } else if (k.moves.length === 0) {
    rows.push(<Row key="none" k={past ? "Hareket yok" : "Henüz hareket yok"} v="" tone="soft" />);
  }
  for (const kind of GROUPS) {
    const ms = (k.moves ?? []).filter((m) => m.kind === kind);
    if (ms.length === 0) continue;
    const total = sum(ms);
    const lines = linesOf(kind, ms, num);
    const names = new Set(ms.map((m) => m.name));
    const head = {
      collection: {k: "Nakit tahsilat", sub: `${ms.length} ödeme`},
      payout: {k: names.size > 1 ? "Danışmanlara verilen" : "Danışmana verilen", sub: "kasadan"},
      devir: {k: `${trDative(ms[0].name)} devir`, sub: undefined},
      expense: {k: "Kasadan gider", sub: undefined},
      refund: {k: "İade", sub: undefined},
      count: {k: "Kasa farkı", sub: k.count ?
        `sayılan ${num(k.count.countedTL)} · kayıtlarda ${num(k.count.systemTL)}` : undefined},
      other: {k: "Diğer", sub: undefined},
    }[kind];
    rows.push(<Row key={kind} k={head.k} sub={head.sub} v={signed(total)}
      tone={kind === "count" ? "warn" : undefined} />);
    lines.forEach((l, i) => rows.push(
      <Row key={`${kind}${i}`} k={l.k} v={l.v} kind={i === lines.length - 1 ? "sub end" : "sub"} />,
    ));
  }
  rows.push(<Row key="total" k={past ? "Kapanış" : "Kasada"} v={signed(k.balanceTL, false)}
    tone={k.balanceTL < 0 ? "bad" : undefined} kind="total" />);

  return (
    <Card title="Kasa hareketi" aside={past ? day : "bugün"} foot={elsewhere.length > 0 && (
      <div className="dm-foot">
        <p>Kasaya dokunmayan: {elsewhere.map((x) =>
          `${trDative(x.name)} ${tl(x.amountTL)} ${x.toSeda ? "devir" : "hakediş"} ` +
          `${HOLDER_TR[x.from] ?? x.from} verildi`).join("; ")}.</p>
      </div>
    )}>
      {rows}
    </Card>
  );
}

// The lines under a group: a collection's seans time and child, in
// time order; whom a payout went to; what an expense was. A devir
// and the count are one figure each.
function linesOf(kind: KasaMoveKind, ms: BoardKasaMove[], num: (n: number) => string):
  {k: string; v: string}[] {
  switch (kind) {
  case "collection":
    return [...ms].sort((a, b) => (a.time ?? "99").localeCompare(b.time ?? "99"))
      .map((m) => ({k: m.time ? `${m.time} · ${m.name}` : m.name, v: num(m.amountTL)}));
  case "payout":
    return ms.map((m) => ({k: `${m.name} · hakediş`, v: num(-m.amountTL)}));
  case "expense":
    return ms.map((m) => ({k: [capTr(m.name), m.note].filter(Boolean).join(" · "),
      v: num(-m.amountTL)}));
  case "refund":
    return ms.map((m) => ({k: m.name, v: num(-m.amountTL)}));
  case "other":
    return ms.map((m) => ({k: m.name, v: m.amountTL < 0 ? `−${num(-m.amountTL)}` : num(m.amountTL)}));
  default:
    return [];
  }
}
